import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, resolveMediaUrl } from "@/lib/api";

const SettingsContext = createContext({ settings: null, refresh: () => {} });
const CACHE_KEY = "mandala_settings_cache";

const normalizeSettings = (data) => ({
  ...data,
  logo: resolveMediaUrl(data.logo),
  favicon: resolveMediaUrl(data.favicon),
  popup: data.popup
    ? { ...data.popup, image: resolveMediaUrl(data.popup.image) }
    : data.popup,
  certifications: Array.isArray(data.certifications)
    ? data.certifications.map((c) => ({
        ...c,
        image: resolveMediaUrl(c.image),
      }))
    : [],
  client_logos: Array.isArray(data.client_logos)
    ? data.client_logos.map((c) => ({
        ...c,
        image: resolveMediaUrl(c.image),
      }))
    : [],
});

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota */
  }
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => readCache());

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const next = normalizeSettings(data);
      setSettings(next);
      writeCache(next);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Dynamic favicon
  useEffect(() => {
    if (!settings) return;

    document.querySelectorAll("link[rel*='icon']").forEach((el) => el.remove());

    const href = settings.favicon
      ? `${resolveMediaUrl(settings.favicon)}${settings.favicon.includes("?") ? "&" : "?"}v=5`
      : `${process.env.PUBLIC_URL || ""}/favicon.png?v=5`;
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = href;
    if (href.includes(".svg")) link.type = "image/svg+xml";
    else if (href.includes(".webp")) link.type = "image/webp";
    else link.type = "image/png";
    link.sizes = "32x32";
    document.head.appendChild(link);

    // Apple touch
    let apple = document.querySelector("link[rel='apple-touch-icon']");
    if (!apple) {
      apple = document.createElement("link");
      apple.rel = "apple-touch-icon";
      document.head.appendChild(apple);
    }
    apple.href = settings.favicon
      ? `${resolveMediaUrl(settings.favicon)}${settings.favicon.includes("?") ? "&" : "?"}v=5`
      : `${process.env.PUBLIC_URL || ""}/apple-touch-icon.png?v=5`;
  }, [settings]);

  return (
    <SettingsContext.Provider value={{ settings, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
